import { notifications } from '@mantine/notifications';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import type React from 'react';

import { ENDPOINTS } from '@/configs/endpoints';
import { ROUTES } from '@/configs/routes';
import {
  DocumentAnalysisResultEntity,
  DocumentAnalysisResultEntityApi,
  documentAnalysisResultSchema,
  isOrganizationAnalysisScope,
  normalizeAnalysisDocumentId,
} from '@/entities/DocumentAnalysis';
import type { AnalysesStore } from '@/stores/global/RootStore/AnalysesStore';
import type { NotificationsStore } from '@/stores/global/RootStore/NotificationsStore';
import { ILocalStore } from '@/stores/interfaces/ILocalStore';
import { MetaStore } from '@/stores/local/shared/MetaStore';
import { StreamingStatusStore } from '@/stores/local/shared/StreamingStatusStore';
import { ToolCallHistoryStore } from '@/stores/local/shared/ToolCallHistoryStore';
import { QuickThoughtsStore } from '@/stores/local/shared/quickThoughts';
import { publishNotificationDismiss } from '@/utils/analysisExecutionStorage';
import api from '@/utils/api';
import { schemeParse } from '@/utils/schemeParse';

import { DocumentAnalysisSseClient } from './DocumentAnalysisSseClient';
import { ExecutionSync } from './ExecutionSync';

type Params = {
  organizationId: string;
  documentId?: string | null;
  analysesStore: AnalysesStore;
  notificationsStore: NotificationsStore;
};

type PrivateFields = '_draftResultText' | '_isStreamCancelled' | '_hasStreamContent';

export class OrganizationPageDocumentsAnalyzeResultStore implements ILocalStore {
  private _organizationId: string;
  private _documentId: string;
  private readonly _isOrganizationScope: boolean;
  private readonly _analysesStore: AnalysesStore;
  private readonly _notificationsStore: NotificationsStore;
  private _abortController: AbortController | null = null;
  private _draftResultText: string | null = null;
  private _isStreamCancelled = false;
  private _hasStreamContent = false;
  private _pendingToolCalls = new Map<string, { displayName: string; createdAt: number }>();
  private readonly _streamingStatus = new StreamingStatusStore({
    getHasReceivedToken: () => this.hasReceivedToken,
    onStatusShown: (status) => {
      if (status.kind && status.kind !== 'call' && status.kind !== 'result') {
        this.toolCallHistory.addStep(status.kind, status.text);
      }
    },
    onQueueEmpty: () => {
      if (!this._hasFormingStatus()) {
        this.toolCallHistory.addStep('result', 'Собрал всю информацию, готовлю ответ');
      }
    },
  });
  readonly toolCallHistory = new ToolCallHistoryStore();
  private readonly _executionSync: ExecutionSync;
  private _currentAnalysisTypeId: number | null = null;
  private readonly _sse = new DocumentAnalysisSseClient();

  hasReceivedToken = false;

  readonly quickThoughts: QuickThoughtsStore;
  readonly executeMeta = new MetaStore();
  readonly existingResultMeta = new MetaStore();

  runningAnalysisTitle: string | undefined;
  result: DocumentAnalysisResultEntity | undefined;

  constructor({ organizationId, documentId, analysesStore, notificationsStore }: Params) {
    makeObservable<OrganizationPageDocumentsAnalyzeResultStore, PrivateFields>(this, {
      result: observable.ref,
      runningAnalysisTitle: observable,
      meta: computed,
      resultText: computed,
      hasReceivedToken: observable,
      _draftResultText: observable.ref,
      _isStreamCancelled: observable,
      _hasStreamContent: observable,
      isStreamCancelled: computed,
      isExternalExecutionActive: computed,
      streamingStatusText: computed,
      hasStreamContent: computed,
      runAnalysis: action.bound,
      loadOrExecute: action.bound,
      cancel: action.bound,
      restartExternalExecution: action.bound,
    });

    this._organizationId = String(organizationId);
    this._documentId = normalizeAnalysisDocumentId(documentId);
    this._isOrganizationScope = isOrganizationAnalysisScope(documentId);
    this._analysesStore = analysesStore;
    this._notificationsStore = notificationsStore;

    this._executionSync = new ExecutionSync({
      organizationId: this._organizationId,
      documentId: this._documentId,
      callbacks: {
        onExternalRunning: (draftText) => {
          runInAction(() => {
            this.existingResultMeta.reset();
            this.executeMeta.setLoadedStartMeta();
            this._draftResultText = draftText;
            this._isStreamCancelled = false;
            this.hasReceivedToken = Boolean(draftText);
            this._hasStreamContent = true;

            if (this.hasReceivedToken) {
              this._streamingStatus.reset();
            }
          });
        },
        onExternalCompletedWithResultId: (analysisResultId) => {
          runInAction(() => {
            this.executeMeta.setLoadedSuccessMeta();
            this.loadExistingResult(analysisResultId);
          });
        },
        onExternalCompletedWithText: (resultText) => {
          runInAction(() => {
            this.existingResultMeta.reset();
            this._draftResultText = resultText;
            this.executeMeta.setLoadedSuccessMeta();
            this._isStreamCancelled = false;
            this._streamingStatus.setText(null);
            this.hasReceivedToken = true;
          });
        },
        onExternalError: () => {
          runInAction(() => {
            this.existingResultMeta.reset();
            this.executeMeta.setLoadedErrorMeta();
            this._draftResultText = null;
            this._isStreamCancelled = false;
            this._streamingStatus.setText(null);
            this.hasReceivedToken = false;
          });
        },
        onExternalCleared: () => {
          runInAction(() => {
            this.existingResultMeta.reset();
            this.executeMeta.setLoadedSuccessMeta();
            this._isStreamCancelled = false;
            this._streamingStatus.setText(null);
            this.hasReceivedToken = false;
          });
        },
      },
    });

    this.quickThoughts = new QuickThoughtsStore({
      organizationId: organizationId,
      notificationsStore: this._notificationsStore,
    });
  }

  private getAnalysisTitleById = (analysisTypeId: number): string => {
    if (this._organizationId && this._documentId) {
      const item = this._analysesStore.getAnalysisById(this._organizationId, this._documentId, analysisTypeId);

      return item?.name ?? `Анализ #${analysisTypeId}`;
    }

    return `Анализ #${analysisTypeId}`;
  };

  get resultText(): string {
    return this._draftResultText ?? this.result?.resultText ?? '';
  }

  get meta(): MetaStore {
    return MetaStore.merge(this.executeMeta, this.existingResultMeta);
  }

  get isStreamCancelled(): boolean {
    return this._isStreamCancelled;
  }

  get isExternalExecutionActive(): boolean {
    return this._executionSync.isExternalExecutionActive && this._currentAnalysisTypeId == null;
  }

  get streamingStatusText(): string | null {
    return this._streamingStatus.statusText;
  }

  get hasStreamContent(): boolean {
    return (
      this._hasStreamContent ||
      this._streamingStatus.hasQueuedStatuses ||
      Boolean(this._streamingStatus.statusText) ||
      this.hasReceivedToken ||
      Boolean(this.resultText)
    );
  }

  private _scheduleStatusRotation(): void {
    this._streamingStatus.scheduleRotation();
  }

  private _clearStatusRotation(): void {
    this._streamingStatus.clearRotation();
  }

  private _appendStreamingStatus(
    textToShow: string,
    analysisTypeId: number,
    kind?: 'call' | 'result' | 'status',
  ): void {
    runInAction(() => {
      this._hasStreamContent = true;
      this._streamingStatus.append(textToShow, kind);
    });

    this._executionSync.publishRunning(analysisTypeId, this._draftResultText ?? textToShow ?? '');
  }

  get analysesMeta(): MetaStore {
    return this._analysesStore.getMeta(this._organizationId, this._documentId);
  }

  private _getLastFormingStatusText(): string | null {
    for (let i = this.toolCallHistory.steps.length - 1; i >= 0; i -= 1) {
      const step = this.toolCallHistory.steps[i];

      if (step?.kind !== 'status' || !('text' in step)) {
        continue;
      }

      const trimmed = step.text.trim();

      if (trimmed.toLowerCase().startsWith('формирую ')) {
        return trimmed;
      }
    }

    return null;
  }

  private _hasFormingStatus(): boolean {
    return this._getLastFormingStatusText() !== null;
  }

  shouldLoadAnalysis(analysisTypeId?: number): boolean {
    const isValidAnalysisTypeId = Number.isFinite(analysisTypeId);

    if (!isValidAnalysisTypeId) {
      return false;
    }

    const meta = this.analysesMeta;

    return Boolean(this._organizationId && (meta.isLoaded || meta.isStaleLoaded));
  }

  private _cancelOngoingStream(): void {
    if (this._abortController) {
      this._abortController.abort();
      this._abortController = null;
    }
  }

  private _updateRunningFlag(analysisTypeId: number, isRunning: boolean): void {
    this._analysesStore.setRunning(this._organizationId, this._documentId, analysisTypeId, isRunning);
    this._analysesStore.setGlobalRunning({
      organizationId: this._organizationId,
      documentId: this._documentId,
      analysisTypeId,
      isRunning,
    });
  }

  private _notifyAnalysisCompleted(analysisTypeId: number): void {
    const analysisTitle = this.getAnalysisTitleById(analysisTypeId);
    const route = this._isOrganizationScope
      ? ROUTES.organization.macro.analyze.result.create(this._organizationId, analysisTypeId)
      : ROUTES.organization.documents.document.analyze.result.create(
          this._organizationId,
          this._documentId,
          analysisTypeId,
        );

    const notificationId = `analysis-finished-${this._organizationId}-${this._documentId}-${analysisTypeId}`;
    let isDismissed = false;

    const broadcastDismiss = () => {
      if (isDismissed) {
        return;
      }
      isDismissed = true;

      publishNotificationDismiss({
        organizationId: this._organizationId,
        documentId: this._documentId,
        analysisTypeId,
      });
    };

    const handleOpenResult = () => {
      window.open(route, '_blank', 'noopener');
      notifications.hide(notificationId);
      broadcastDismiss();
    };

    const isCloseClick = (event?: React.MouseEvent<HTMLElement>): boolean => {
      const native = event?.nativeEvent as Event & { _notificationClosedByButton?: boolean };

      if (native?._notificationClosedByButton) {
        return true;
      }

      const path = native?.composedPath?.() ?? [];

      return path.some((node) => {
        const el = node as HTMLElement | null;

        return el?.dataset?.notificationClose != null || el?.getAttribute?.('data-close-button') != null;
      });
    };

    this._notificationsStore.showSuccessAction({
      title: 'Анализ завершен',
      message: analysisTitle,
      position: 'top-right',
      onClick: (event) => {
        if (event?.defaultPrevented || isCloseClick(event)) {
          return;
        }
        handleOpenResult();
      },
      id: notificationId,
      autoClose: 30000,
      onClose: broadcastDismiss,
      closeButtonProps: {
        onClickCapture: (event: React.MouseEvent<HTMLElement>) => {
          const native = event.nativeEvent as Event & { _notificationClosedByButton?: boolean };

          native._notificationClosedByButton = true;
        },
        onMouseDownCapture: (event: React.MouseEvent<HTMLElement>) => {
          const native = event.nativeEvent as Event & { _notificationClosedByButton?: boolean };

          native._notificationClosedByButton = true;
        },
        onClick: (event: React.MouseEvent<HTMLElement>) => {
          const native = event.nativeEvent as Event & { _notificationClosedByButton?: boolean };

          native._notificationClosedByButton = true;
          event.preventDefault();
          event.stopPropagation();
          notifications.hide(notificationId);
          broadcastDismiss();
        },
        onMouseDown: (event: React.MouseEvent<HTMLElement>) => {
          const native = event.nativeEvent as Event & { _notificationClosedByButton?: boolean };

          native._notificationClosedByButton = true;
          event.preventDefault();
          event.stopPropagation();
        },
      },
    });
  }

  private loadExistingResult = async (analysisResultId: number): Promise<void> => {
    this.existingResultMeta.setLoadedStartMeta();
    const endpoint = this._isOrganizationScope
      ? ENDPOINTS.analyze.organizationResult(this._organizationId, analysisResultId)
      : ENDPOINTS.analyze.result(this._organizationId, this._documentId, analysisResultId);

    const response = await api<DocumentAnalysisResultEntityApi>({ endpoint, method: 'GET' });

    try {
      if (response.isError) {
        this.existingResultMeta.setLoadedErrorMeta();

        return;
      }

      const parsed = schemeParse(documentAnalysisResultSchema, response.data);
      const result = parsed.success ? parsed.data : undefined;

      runInAction(() => {
        this.result = result;
        this._draftResultText = null;
        this._streamingStatus.setText(null);
        this.existingResultMeta.setLoadedSuccessMeta();
        this._isStreamCancelled = false;
      });
    } catch {
      this.existingResultMeta.setLoadedErrorMeta();
    }
  };

  runAnalysis = async (analysisTypeId: number): Promise<void> => {
    if (this._isOrganizationScope) {
      this.executeMeta.setLoadedSuccessMeta();
      this._pendingToolCalls.clear();
      this.toolCallHistory.reset();
      this.existingResultMeta.reset();

      return;
    }

    const analysisTitle = this.getAnalysisTitleById(analysisTypeId);

    this.runningAnalysisTitle = analysisTitle;
    this.executeMeta.setLoadedStartMeta();
    this.existingResultMeta.reset();
    this._isStreamCancelled = false;
    this._pendingToolCalls.clear();
    this.toolCallHistory.reset();
    this.toolCallHistory.start();

    this._updateRunningFlag(analysisTypeId, true);
    this._currentAnalysisTypeId = analysisTypeId;
    this._executionSync.observe(analysisTypeId);
    this._cancelOngoingStream();
    this._abortController = new AbortController();
    let hasStreamError = false;
    let isStreamStarted = false;

    const markStreamStarted = () => {
      if (isStreamStarted) {
        return;
      }
      isStreamStarted = true;
      runInAction(() => {
        this._draftResultText = '';
        this._executionSync.publishRunning(analysisTypeId, '', { force: true });
        this._streamingStatus.reset();
        this._hasStreamContent = true;
        this.hasReceivedToken = false;
      });
    };

    runInAction(() => {
      this._draftResultText = '';
      this._streamingStatus.reset();
      this._hasStreamContent = false;
      this.hasReceivedToken = false;
    });

    try {
      await this._sse.stream({
        url: ENDPOINTS.analyze.executeStream(this._organizationId, this._documentId),
        body: { analysis_type_id: analysisTypeId },
        signal: this._abortController.signal,
        handlers: {
          onStart: markStreamStarted,
          onToolCall: (payload) => {
            markStreamStarted();
            this._pendingToolCalls.set(payload.id, {
              displayName: payload.displayName,
              createdAt: Date.now(),
            });
            const textToShow = payload.displayName.trim().length > 0 ? payload.displayName : 'Подготавливаю данные...';

            this._appendStreamingStatus(textToShow, analysisTypeId, 'call');
          },
          onToolCallResult: (payload) => {
            markStreamStarted();
            const pending = payload.toolCallId ? this._pendingToolCalls.get(payload.toolCallId) : undefined;

            if (pending) {
              this.toolCallHistory.addToolCallStep(payload.toolCallId, pending.displayName, payload.content);
              this._pendingToolCalls.delete(payload.toolCallId);
            } else {
              this.toolCallHistory.addStep('result', payload.content);
            }
            const textToShow = pending ? pending.displayName : 'Получаю результаты инструмента...';

            this._appendStreamingStatus(textToShow, analysisTypeId, 'result');
          },
          onStatus: (content) => {
            markStreamStarted();
            const textToShow = content && content.trim().length > 0 ? content.trim() : '';

            if (!textToShow) {
              return;
            }

            this._appendStreamingStatus(textToShow, analysisTypeId, 'status');
          },
          onInformationSources: (content) => {
            markStreamStarted();
            const textToShow = content && content.trim().length > 0 ? content.trim() : '';

            if (!textToShow) {
              return;
            }

            this.toolCallHistory.addStep('information_sources', textToShow);
          },
          onToken: (content) => {
            markStreamStarted();
            runInAction(() => {
              this._draftResultText = content;
              this.hasReceivedToken = true;
              this._hasStreamContent = true;

              if (!this._streamingStatus.hasQueuedStatuses) {
                this._scheduleStatusRotation();
              }
            });
            this._executionSync.publishRunning(analysisTypeId, content);
          },
          onComplete: (event) => {
            const parsed = schemeParse(documentAnalysisResultSchema, event.raw as DocumentAnalysisResultEntityApi);

            if (parsed.success) {
              runInAction(() => {
                this.result = parsed.data;
                this._draftResultText = null;
                this._isStreamCancelled = false;
                this._streamingStatus.reset();
              });
              this._executionSync.publishCompleted(analysisTypeId, {
                analysisResultId: parsed.data.id,
                analysisTitle,
              });
              this._updateRunningFlag(analysisTypeId, false);
              this._analysesStore.setAnalysisResultId(
                this._organizationId,
                this._documentId,
                parsed.data.analysisTypeId,
                parsed.data.id,
              );
              this._notifyAnalysisCompleted(analysisTypeId);
            } else {
              runInAction(() => {
                this._draftResultText = event.text;
                this._streamingStatus.reset();
                this.hasReceivedToken = true;
                this._hasStreamContent = true;
              });
              this._executionSync.publishCompleted(analysisTypeId, {
                analysisResultId: null,
                resultText: event.text,
                analysisTitle,
              });
              this._updateRunningFlag(analysisTypeId, false);
              this._notifyAnalysisCompleted(analysisTypeId);
            }
          },
          onDone: () => {
            if (!hasStreamError) {
              this.executeMeta.setLoadedSuccessMeta();
            }
            this.toolCallHistory.finish();
          },
          onError: () => {
            hasStreamError = true;
            runInAction(() => {
              this.executeMeta.setLoadedErrorMeta();
              this._draftResultText = null;
              this._isStreamCancelled = false;
              this._streamingStatus.reset();
              this.hasReceivedToken = false;
              this._hasStreamContent = false;
            });
            this._pendingToolCalls.clear();
            this.toolCallHistory.reset();
            this._executionSync.publishError(analysisTypeId);
            this._updateRunningFlag(analysisTypeId, false);
            this._cancelOngoingStream();
          },
        },
      });
    } catch (error) {
      if ((error as Error)?.name === 'AbortError') {
        return;
      }

      this.executeMeta.setLoadedErrorMeta();
      this._pendingToolCalls.clear();
      this.toolCallHistory.reset();
      this._executionSync.publishError(analysisTypeId);
      this._updateRunningFlag(analysisTypeId, false);
    } finally {
      this._cancelOngoingStream();
      if (!this.executeMeta.isError && this.executeMeta.isLoading) {
        this.executeMeta.setLoadedSuccessMeta();
      }
      this._executionSync.scheduleCleanup(analysisTypeId);
      this._currentAnalysisTypeId = null;
      this._updateRunningFlag(analysisTypeId, false);
      runInAction(() => {
        this._streamingStatus.reset();
        this.hasReceivedToken = false;
        this._hasStreamContent = false;
      });
    }
  };

  cancel = (): void => {
    const currentAnalysisId = this._currentAnalysisTypeId ?? this._executionSync.analysisTypeId;

    if (!this._abortController && currentAnalysisId == null) {
      return;
    }

    const draftText = this._draftResultText ?? undefined;

    this._cancelOngoingStream();
    this.executeMeta.setLoadedSuccessMeta();
    this._isStreamCancelled = true;
    this._streamingStatus.reset();
    this.hasReceivedToken = false;
    this._hasStreamContent = false;
    this._pendingToolCalls.clear();
    this.toolCallHistory.reset();
    this.existingResultMeta.reset();

    if (currentAnalysisId != null) {
      this._updateRunningFlag(currentAnalysisId, false);
      this._executionSync.publishCompleted(currentAnalysisId, {
        analysisResultId: null,
        resultText: draftText,
        analysisTitle: this.runningAnalysisTitle,
      });
      this._executionSync.scheduleCleanup(currentAnalysisId);
      this._currentAnalysisTypeId = null;
    }
  };

  restartExternalExecution = async (analysisTypeId: number): Promise<void> => {
    if (this._isOrganizationScope) {
      return;
    }

    if (!Number.isFinite(analysisTypeId) || !this._organizationId) {
      return;
    }

    this._executionSync.isExternalExecutionActive = false;
    this._executionSync.scheduleCleanup(analysisTypeId);
    this._updateRunningFlag(analysisTypeId, false);

    await this.runAnalysis(analysisTypeId);
  };

  loadOrExecute = async (analysisTypeId: number): Promise<void> => {
    if (this.meta.isLoading) {
      return;
    }

    this.runningAnalysisTitle = this.getAnalysisTitleById(analysisTypeId);

    this._executionSync.observe(analysisTypeId);

    if (this._executionSync.isExternalExecutionActive) {
      return;
    }

    const current = this._analysesStore.getAnalysisById(this._organizationId, this._documentId, analysisTypeId);
    const existingResultId = current?.analysis_result_id ?? null;

    if (existingResultId !== null) {
      await this.loadExistingResult(existingResultId);

      return;
    }

    if (this._isOrganizationScope) {
      this.executeMeta.setLoadedSuccessMeta();

      return;
    }

    await this.runAnalysis(analysisTypeId);
  };

  refreshAnalysis = (analysisTypeId: number): Promise<void> => this.runAnalysis(analysisTypeId);

  destroy(): void {
    this._executionSync.destroy();
    this._cancelOngoingStream();
    this._clearStatusRotation();
    this.quickThoughts.destroy();
    this.toolCallHistory.destroy();
  }
}
